import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Popper,
  Grow,
  ClickAwayListener,
  Collapse,
  ListItemIcon,
  Typography,
  Fade,
  TextField,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LoginIcon from '@mui/icons-material/Login';
import SearchIcon from '@mui/icons-material/Search';

import logo from '../assets/logoMain.png';
import { useAuth } from '../authContext';

// --- Styled components (Source Sans Pro look; clean, compact) ---
const NavButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  color: theme.palette.text.primary,
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1.05rem',
  letterSpacing: 0.1,
  fontFamily: 'inherit', // inherit Source Sans Pro from theme
  padding: theme.spacing(1.25, 1.75),
  borderRadius: 10,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  // Underline accent on hover/active (inspired by modern navbars)
  '&::after': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(1.25),
    right: theme.spacing(1.25),
    bottom: 6,
    height: 2,
    borderRadius: 1,
    backgroundColor: alpha(theme.palette.primary.main, 0),
    transition: 'background-color .2s ease, transform .2s ease',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
  },
  '&:hover::after': {
    backgroundColor: theme.palette.primary.main,
    transform: 'scaleX(1)',
  },
  '&.active::after': {
    backgroundColor: theme.palette.primary.main,
    transform: 'scaleX(1)',
  },
}));

const SubmenuPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[6],
  background: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  width: 'auto',
  minWidth: 260,
  padding: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
}));

const SubmenuItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 1.5),
  borderRadius: 10,
  cursor: 'pointer',
  transition: 'background-color .15s ease, transform .15s ease',
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
  '& svg': { marginLeft: 'auto', fontSize: '1rem', color: theme.palette.text.secondary },
}));


const MobileListItem = styled(ListItem)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
}));

function Navbar({ darkMode, setDarkMode }) {
  const { currentUser, signOut } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const appBarRef = useRef(null);

  // Compute which menu section is active
  const menuItems = [
    {
      label: t('nav.aboutUs'),
      path: '/about-us',
      // subItems: [
        // { label: t('nav.team'), path: '/about-us/team' }, TODO re-enable when team page is added
        // { label: t('nav.history'), path: '/about-us/history' }, TODO re-enable when history page is added
      // ],
    },
    { label: t('nav.news'), path: '/news' },
    {
        label: t('nav.events'),
        path: '/events',
        ...(isMobile
          ? {
              subItems: [
                { label: 'Events', path: '/events#events' },
                { label: 'Calls', path: '/events#calls' },
                { label: 'Past Events', path: '/events#past-events' },

              ],
            }
          : {}),
      },    {
      label: t('nav.partner'),
      path: '/partner',
      // subItems: [
      //    { label: t('nav.becomePartner'), path: '/partner/join' },
      //    { label: t('nav.ourPartners'), path: '/partner/list' },
      // ],
    },
    { label: t('nav.understandingEurope'), path: '/ueaustria' },
    { label: t('nav.membership'), path: '/membership' },
    { label: t('nav.contact'), path: '/contact' },
  ];
  const activeIdx = menuItems.findIndex(i =>
    location.pathname.startsWith(i.path)
  );

  // Mobile submenus state, open active by default
  const [mobileSubOpen, setMobileSubOpen] = useState(() => ({
    ...(activeIdx >= 0 ? { [activeIdx]: true } : {}),
  }));
  const toggleMobileSub = idx =>
    setMobileSubOpen(prev => ({ ...prev, [idx]: !prev[idx] }));

  // Reorder so active section is first
  const otherItems = menuItems.filter((_, i) => i !== activeIdx);
  const orderedMenu =
    activeIdx >= 0 ? [menuItems[activeIdx], ...otherItems] : menuItems;

  // Desktop hover state
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const btnRefs = useRef([]);

  // Search dialog
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = e => {
    e.preventDefault();
    navigate(`/search?query=${searchQuery}`);
    setOpenSearchDialog(false);
  };

  const handleDrawerToggle = () => setMobileOpen(prev => !prev);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  return (
    <>
      <AppBar
        ref={appBarRef}
        position="sticky"
        sx={{
          bgcolor: theme => theme.palette.background.paper,          color: 'text.primary',
          borderBottom: 'none',
          boxShadow: theme => theme.shadows[1],
          zIndex: theme => theme.zIndex.appBar + 1,
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            px: isMobile ? 1 : 3,
            py: isMobile ? 1 : 1.25,
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              width: isMobile ? 160 : 220,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          />

          {/* Desktop menu */}
          {!isMobile && (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              {menuItems.map((item, idx) => {
                const anchorEl = btnRefs.current[idx];
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Box
                    key={idx}
                    sx={{ position: 'relative', mx: 0.5 }}
                    onMouseEnter={() => setHoveredIndex(idx)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <NavButton
                      ref={el => (btnRefs.current[idx] = el)}
                      className={isActive ? 'active' : undefined}
                      component={RouterLink}
                      to={item.path}
                      endIcon={
                        item.subItems ? (
                          hoveredIndex === idx ? (
                            <ExpandLess fontSize="small" />
                          ) : (
                            <ExpandMore fontSize="small" />
                          )
                        ) : null
                      }
                    >
                      {item.label}
                    </NavButton>

                    {item.subItems && anchorEl && (
                      <Popper
                        open={hoveredIndex === idx}
                        anchorEl={anchorEl}
                        placement="bottom-start"
                        style={{ zIndex: 1300 }}
                        transition
                        variant="h6" sx={{ fontWeight: 400 }}
                      >
                        {({ TransitionProps }) => (
                          <ClickAwayListener
                            onClickAway={() => setHoveredIndex(null)}
                          >
                            <Grow
                              {...TransitionProps}
                              style={{ transformOrigin: 'top left' }}
                            >
                              <Box sx={{ position: 'relative', width: '100%' }}>
                                                                <SubmenuPaper>
                                  <List dense sx={{ py: 0.5 }}>
                                    {item.subItems.map((sub, sidx) => (
                                      <RouterLink
                                        key={sidx}
                                        to={sub.path}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                        onClick={() => setHoveredIndex(null)}
                                      >
                                        <SubmenuItem>
                                          <Typography variant="body1" sx={{ fontWeight: 400 }}>
                                            {sub.label}
                                          </Typography>
                                          <ChevronRightIcon />
                                        </SubmenuItem>
                                      </RouterLink>
                                    ))}
                                  </List>
                                </SubmenuPaper>
                              </Box>
                            </Grow>
                          </ClickAwayListener>
                        )}
                      </Popper>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Right icons */}
          {isMobile ? (
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              sx={{ ml: 'auto' }}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? (
                <CloseIcon sx={{ fontSize: '2rem' }} />
              ) : (
                <MenuIcon sx={{ fontSize: '2rem' }} />
              )}
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                color="inherit"
                onClick={() => setOpenSearchDialog(true)}
                aria-label="Search"
              >
                <SearchIcon sx={{ fontSize: '1.6rem' }} />
              </IconButton>
              {currentUser ? (
                <>
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/user-dashboard')}
                    aria-label="Account"
                  >
                    <AccountCircle sx={{ fontSize: '1.9rem' }} />
                  </IconButton>
                  <Button
                    onClick={handleSignOut}
                    sx={{ textTransform: 'none', fontSize: '0.95rem', ml: 1 }}
                  >
                    {t('nav.signOut')}
                  </Button>
                </>
              ) : (
                <IconButton
                  color="inherit"
                  component={RouterLink}
                  to="/sign-in"
                  aria-label="Sign in"
                  
                >
                  <LoginIcon sx={{ fontSize: '1.9rem' }} />
                </IconButton>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        BackdropProps={{ invisible: false }}
        SlideProps={{ timeout: 250 }}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100vw',
            background: theme => theme.palette.background.paper,
          },
          zIndex: theme => theme.zIndex.modal + 2,
        }}
      >
        <Fade in={mobileOpen} timeout={250}>
          <Box
            sx={{
              width: '100vw',
              height: '100vh',
              pt: 8,
              position: 'relative',
            }}
          >
            <Box sx={{ position: 'absolute', top: 0, right: 0, p: 2 }}>
              <IconButton onClick={handleDrawerToggle} aria-label="Close menu">
                <CloseIcon sx={{ fontSize: '2rem' }} />
              </IconButton>
            </Box>
            <List>
              {orderedMenu.map((item, idx) => (
                <Box key={idx}>
                  <ListItem
                    onClick={() =>
                      item.subItems
                        ? toggleMobileSub(idx)
                        : (navigate(item.path), setMobileOpen(false))
                    }
                    sx={{ px: 3, py: 1.75 }}
                    button
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700 }}
                    >
                      {item.label}
                    </Typography>
                    {item.subItems &&
                      (mobileSubOpen[idx] ? (
                        <ExpandLess sx={{ fontSize: '1.6rem' }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: '1.6rem' }} />
                      ))}
                  </ListItem>
                  {item.subItems && (
                    <Collapse in={mobileSubOpen[idx]} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {item.subItems.map((sub, sidx) => (
                          <MobileListItem
                            key={sidx}
                            onClick={() => (navigate(sub.path), setMobileOpen(false))}
                            button
                          >
                            <ListItemIcon>
                              <ChevronRightIcon sx={{ fontSize: '1.1rem' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body1" sx={{ fontWeight: 400 }}>
                                  {sub.label}
                                </Typography>
                              }
                            />
                          </MobileListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </Box>
              ))}
            </List>
          </Box>
        </Fade>
      </Drawer>

      {/* Search Dialog */}
      <Dialog
        open={openSearchDialog}
        onClose={() => setOpenSearchDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 2,
            minWidth: isMobile ? '90vw' : 520,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
          {t('nav.search')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSearch}>
            <TextField
              autoFocus
              fullWidth
              placeholder={t('nav.search')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1 }} />,
              }}
              variant="outlined"
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Navbar;
